import { Controller } from "@hotwired/stimulus"
import { toast } from "../services/toast"

// Controller for managing print queue functionality
// Handles selection, printing via browser API, and marking as printed
export default class extends Controller {
  static targets = [
    "qrSelectAll",
    "barcodeSelectAll",
    "qrSelectedCount",
    "barcodeSelectedCount",
    "qrPrintBtn",
    "qrDownloadBtn",
    "barcodePrintBtn",
    "barcodeDownloadBtn",
    "qrTemplate",
    "barcodeTemplate",
    "qrJobsList",
    "barcodeJobsList",
    "printFrame",
    "headerBadge"
  ]

  static values = {
    pendingCountUrl: String,
    generatePdfUrl: String,
    markPrintedUrl: String,
    pollingInterval: { type: Number, default: 30000 }
  }

  connect() {
    this.startPolling()
  }

  disconnect() {
    this.stopPolling()
  }

  // Start polling for pending count updates
  startPolling() {
    this.pollTimer = setInterval(() => {
      this.fetchPendingCount()
    }, this.pollingIntervalValue)
  }

  stopPolling() {
    if (this.pollTimer) {
      clearInterval(this.pollTimer)
    }
  }

  async fetchPendingCount() {
    try {
      const response = await fetch(this.pendingCountUrlValue, {
        headers: {
          "Accept": "application/json"
        }
      })

      if (response.ok) {
        const data = await response.json()
        this.updateHeaderBadge(data.total)
        this.updateSidebarBadge(data.total)
      }
    } catch (error) {
      console.debug("Error fetching pending count:", error)
    }
  }

  updateHeaderBadge(count) {
    if (this.hasHeaderBadgeTarget) {
      this.headerBadgeTarget.textContent = `${count} pendientes`
    }
  }

  updateSidebarBadge(count) {
    // Update sidebar badge via notification controller target
    const badge = document.querySelector('[data-notification-target="printQueueBadge"]')
    if (badge) {
      if (count > 0) {
        badge.textContent = count
        badge.style.display = ""
      } else {
        badge.style.display = "none"
      }
    }
  }

  // Toggle all QR checkboxes
  toggleAllQr(event) {
    const checked = event.target.checked
    this.element.querySelectorAll(".qr-checkbox:not(:disabled)").forEach(checkbox => {
      checkbox.checked = checked
    })
    this.updateSelection()
  }

  // Toggle all barcode checkboxes
  toggleAllBarcode(event) {
    const checked = event.target.checked
    this.element.querySelectorAll(".barcode-checkbox:not(:disabled)").forEach(checkbox => {
      checkbox.checked = checked
    })
    this.updateSelection()
  }

  // Toggle all checkboxes in a group
  toggleGroup(event) {
    const checkbox = event.target
    const group = checkbox.dataset.group
    const checked = checkbox.checked

    this.element.querySelectorAll(`.job-checkbox[data-group="${group}"]:not(:disabled)`).forEach(cb => {
      cb.checked = checked
    })
    this.updateSelection()
  }

  // Update selection counts and button states
  updateSelection() {
    const qrChecked = this.element.querySelectorAll(".qr-checkbox:checked").length
    const barcodeChecked = this.element.querySelectorAll(".barcode-checkbox:checked").length

    // Update counts
    if (this.hasQrSelectedCountTarget) {
      this.qrSelectedCountTarget.textContent = `${qrChecked} seleccionada${qrChecked !== 1 ? "s" : ""}`
    }
    if (this.hasBarcodeSelectedCountTarget) {
      this.barcodeSelectedCountTarget.textContent = `${barcodeChecked} seleccionada${barcodeChecked !== 1 ? "s" : ""}`
    }

    // Update button states
    if (this.hasQrPrintBtnTarget) {
      this.qrPrintBtnTarget.disabled = qrChecked === 0
    }
    if (this.hasQrDownloadBtnTarget) {
      this.qrDownloadBtnTarget.disabled = qrChecked === 0
    }
    if (this.hasBarcodePrintBtnTarget) {
      this.barcodePrintBtnTarget.disabled = barcodeChecked === 0
    }
    if (this.hasBarcodeDownloadBtnTarget) {
      this.barcodeDownloadBtnTarget.disabled = barcodeChecked === 0
    }

    // Update select all checkboxes
    this.updateSelectAllState()
  }

  updateSelectAllState() {
    if (this.hasQrSelectAllTarget) {
      const allQr = this.element.querySelectorAll(".qr-checkbox:not(:disabled)")
      const checkedQr = this.element.querySelectorAll(".qr-checkbox:checked")
      this.qrSelectAllTarget.checked = allQr.length > 0 && allQr.length === checkedQr.length
      this.qrSelectAllTarget.indeterminate = checkedQr.length > 0 && checkedQr.length < allQr.length
    }

    if (this.hasBarcodeSelectAllTarget) {
      const allBarcode = this.element.querySelectorAll(".barcode-checkbox:not(:disabled)")
      const checkedBarcode = this.element.querySelectorAll(".barcode-checkbox:checked")
      this.barcodeSelectAllTarget.checked = allBarcode.length > 0 && allBarcode.length === checkedBarcode.length
      this.barcodeSelectAllTarget.indeterminate = checkedBarcode.length > 0 && checkedBarcode.length < allBarcode.length
    }
  }

  // Get selected job IDs for a type
  getSelectedIds(type) {
    const selector = type === "qr" ? ".qr-checkbox:checked" : ".barcode-checkbox:checked"
    return Array.from(this.element.querySelectorAll(selector)).map(cb => cb.value)
  }

  // Print QR labels
  async printQr() {
    await this.print("qr", this.qrTemplateTarget?.value)
  }

  // Print barcode labels
  async printBarcode() {
    await this.print("barcode", this.barcodeTemplateTarget?.value)
  }

  // Download QR PDF
  async downloadQr() {
    await this.downloadPdf("qr", this.qrTemplateTarget?.value)
  }

  // Download barcode PDF
  async downloadBarcode() {
    await this.downloadPdf("barcode", this.barcodeTemplateTarget?.value)
  }

  // Print using browser print dialog
  async print(jobType, templateId) {
    const jobIds = this.getSelectedIds(jobType)
    
    if (jobIds.length === 0) {
      toast.warning("No hay etiquetas seleccionadas")
      return
    }

    try {
      // Show loading state
      const btn = jobType === "qr" ? this.qrPrintBtnTarget : this.barcodePrintBtnTarget
      const originalText = btn.innerHTML
      btn.disabled = true
      btn.innerHTML = '<span class="spinner-border spinner-border-sm"></span> Preparando...'

      // Fetch PDF
      const response = await this.fetchPdf(jobIds, templateId, jobType)
      
      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || "Error al generar PDF")
      }

      const blob = await response.blob()
      const pdfUrl = URL.createObjectURL(blob)

      // Print via iframe
      await this.printViaIframe(pdfUrl, jobIds, jobType)

      // Restore button
      btn.innerHTML = originalText
      btn.disabled = false

    } catch (error) {
      console.error("Print error:", error)
      toast.error(error.message || "Error al imprimir")
      
      // Restore button state
      const btn = jobType === "qr" ? this.qrPrintBtnTarget : this.barcodePrintBtnTarget
      if (btn) {
        btn.disabled = false
        btn.innerHTML = jobType === "qr" ? 
          '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" style="width:1rem;height:1rem"><path stroke-linecap="round" stroke-linejoin="round" d="M6.72 13.829c-.24.03-.48.062-.72.096m.72-.096a42.415 42.415 0 0110.56 0m-10.56 0L6.34 18m10.94-4.171c.24.03.48.062.72.096m-.72-.096L17.66 18m0 0l.229 2.523a1.125 1.125 0 01-1.12 1.227H7.231c-.662 0-1.18-.568-1.12-1.227L6.34 18m11.318 0h1.091A2.25 2.25 0 0021 15.75V9.456c0-1.081-.768-2.015-1.837-2.175a48.055 48.055 0 00-1.913-.247M6.34 18H5.25A2.25 2.25 0 013 15.75V9.456c0-1.081.768-2.015 1.837-2.175a48.041 48.041 0 011.913-.247m10.5 0a48.536 48.536 0 00-10.5 0m10.5 0V3.375c0-.621-.504-1.125-1.125-1.125h-8.25c-.621 0-1.125.504-1.125 1.125v3.659M18 10.5h.008v.008H18V10.5zm-3 0h.008v.008H15V10.5z" /></svg> Imprimir QR' :
          '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" style="width:1rem;height:1rem"><path stroke-linecap="round" stroke-linejoin="round" d="M6.72 13.829c-.24.03-.48.062-.72.096m.72-.096a42.415 42.415 0 0110.56 0m-10.56 0L6.34 18m10.94-4.171c.24.03.48.062.72.096m-.72-.096L17.66 18m0 0l.229 2.523a1.125 1.125 0 01-1.12 1.227H7.231c-.662 0-1.18-.568-1.12-1.227L6.34 18m11.318 0h1.091A2.25 2.25 0 0021 15.75V9.456c0-1.081-.768-2.015-1.837-2.175a48.055 48.055 0 00-1.913-.247M6.34 18H5.25A2.25 2.25 0 013 15.75V9.456c0-1.081.768-2.015 1.837-2.175a48.041 48.041 0 011.913-.247m10.5 0a48.536 48.536 0 00-10.5 0m10.5 0V3.375c0-.621-.504-1.125-1.125-1.125h-8.25c-.621 0-1.125.504-1.125 1.125v3.659M18 10.5h.008v.008H18V10.5zm-3 0h.008v.008H15V10.5z" /></svg> Imprimir Códigos'
      }
    }
  }

  // Print PDF via hidden iframe
  async printViaIframe(pdfUrl, jobIds, jobType) {
    return new Promise((resolve, reject) => {
      const iframe = this.printFrameTarget
      
      iframe.onload = () => {
        try {
          // Small delay to ensure PDF is rendered
          setTimeout(() => {
            iframe.contentWindow.print()
            
            // After print dialog closes, ask to mark as printed
            setTimeout(() => {
              this.promptMarkAsPrinted(jobIds, jobType)
              URL.revokeObjectURL(pdfUrl)
              resolve()
            }, 500)
          }, 500)
        } catch (error) {
          reject(error)
        }
      }

      iframe.onerror = () => {
        URL.revokeObjectURL(pdfUrl)
        reject(new Error("Error loading PDF"))
      }

      iframe.src = pdfUrl
    })
  }

  // Prompt user to mark labels as printed
  promptMarkAsPrinted(jobIds, jobType) {
    const count = jobIds.length
    const label = count === 1 ? "etiqueta" : "etiquetas"
    
    if (confirm(`¿Se imprimieron correctamente ${count} ${label}? \n\nSi es así, se marcarán como impresas.`)) {
      this.markAsPrinted(jobIds)
    }
  }

  // Mark jobs as printed
  async markAsPrinted(jobIds) {
    try {
      const token = document.querySelector('meta[name="csrf-token"]')?.content
      
      const response = await fetch(this.markPrintedUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          ...(token ? { "X-CSRF-Token": token } : {})
        },
        body: JSON.stringify({ job_ids: jobIds })
      })

      const data = await response.json()

      if (response.ok) {
        toast.success(data.message || "Etiquetas marcadas como impresas")
        // Reload page to update the list
        window.location.reload()
      } else {
        toast.error(data.error || "Error al marcar como impresas")
      }
    } catch (error) {
      console.error("Mark printed error:", error)
      toast.error("Error al marcar como impresas")
    }
  }

  // Download PDF without printing
  async downloadPdf(jobType, templateId) {
    const jobIds = this.getSelectedIds(jobType)
    
    if (jobIds.length === 0) {
      toast.warning("No hay etiquetas seleccionadas")
      return
    }

    try {
      const btn = jobType === "qr" ? this.qrDownloadBtnTarget : this.barcodeDownloadBtnTarget
      const originalText = btn.innerHTML
      btn.disabled = true
      btn.innerHTML = '<span class="spinner-border spinner-border-sm"></span> Generando...'

      const response = await this.fetchPdf(jobIds, templateId, jobType)
      
      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || "Error al generar PDF")
      }

      const blob = await response.blob()
      const pdfUrl = URL.createObjectURL(blob)

      // Create download link
      const link = document.createElement("a")
      link.href = pdfUrl
      link.download = `etiquetas_${jobType}_${new Date().toISOString().slice(0, 10)}.pdf`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      URL.revokeObjectURL(pdfUrl)

      btn.innerHTML = originalText
      btn.disabled = false

      toast.success("PDF descargado correctamente")

    } catch (error) {
      console.error("Download error:", error)
      toast.error(error.message || "Error al descargar PDF")
      
      const btn = jobType === "qr" ? this.qrDownloadBtnTarget : this.barcodeDownloadBtnTarget
      if (btn) {
        btn.disabled = false
      }
    }
  }

  // Fetch PDF from server
  async fetchPdf(jobIds, templateId, jobType) {
    const token = document.querySelector('meta[name="csrf-token"]')?.content

    return fetch(this.generatePdfUrlValue, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { "X-CSRF-Token": token } : {})
      },
      body: JSON.stringify({
        job_ids: jobIds,
        template_id: templateId,
        job_type: jobType
      })
    })
  }
}
